import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../store';
import {
  worksService,
  type ComposerWork,
  type ComposerWorksPagination,
} from '../../services/works';
import { composerService, type CatalogComposer } from '../../services/composers';

type LoadComposerWorksArgs = {
  composerId: string;
  force?: boolean;
  spotifyArtistId?: string;
  limit?: number;
  offset?: number;
};

type ComposerWorkStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

type ComposerWorkEntry = {
  items: ComposerWork[];
  pages: Record<number, ComposerWork[]>;
  pagination: ComposerWorksPagination | null;
  fullCatalog?: ComposerWork[];
  status: ComposerWorkStatus;
  error: string | null;
  resolvedComposerId?: string;
  lastRequest?: {
    offset: number;
    limit?: number;
  };
};

type ComposerWorksState = {
  byComposerId: Record<string, ComposerWorkEntry>;
};

const DEFAULT_PAGE_SIZE = 50;

type LoadComposerWorksSuccess = {
  composerId: string;
  works: ComposerWork[];
  pagination: ComposerWorksPagination;
  resolvedComposerId?: string;
  fullCollection?: ComposerWork[];
};

type LoadComposerWorksOptions = {
  force?: boolean;
  spotifyArtistId?: string;
  limit?: number;
  offset?: number;
};

const ensureEntry = (state: ComposerWorksState, composerId: string): ComposerWorkEntry => {
  const existing = state.byComposerId[composerId];
  if (existing) return existing;
  const next: ComposerWorkEntry = {
    items: [],
    pages: {},
    pagination: null,
    fullCatalog: undefined,
    status: 'idle',
    error: null,
  };
  state.byComposerId[composerId] = next;
  return next;
};

const sanitizeLimit = (value?: number): number | undefined => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }
  const floored = Math.floor(value);
  return floored > 0 ? floored : undefined;
};

const sanitizeOffset = (value?: number): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 0;
  }
  const floored = Math.floor(value);
  return floored >= 0 ? floored : 0;
};

const resolvePageNumber = (offset: number, limit: number): number => {
  const safeLimit = limit > 0 ? limit : DEFAULT_PAGE_SIZE;
  return Math.floor(offset / safeLimit) + 1;
};

const aggregatePages = (pages: Record<number, ComposerWork[]>): ComposerWork[] => {
  return Object.keys(pages)
    .map((pageKey) => Number(pageKey))
    .filter((page) => Number.isFinite(page) && page >= 1)
    .sort((a, b) => a - b)
    .flatMap((page) => pages[page] ?? []);
};

const resolveComposerId = (composer: CatalogComposer | null): string | undefined => {
  if (!composer) return undefined;

  const { id } = composer;

  if (typeof id === 'string' && id.trim().length) {
    return id;
  }

  if (typeof id === 'number' && Number.isFinite(id)) {
    return String(id);
  }

  const legacyId = (composer as { composerId?: unknown }).composerId;

  if (typeof legacyId === 'string' && legacyId.trim().length) {
    return legacyId;
  }

  if (typeof legacyId === 'number' && Number.isFinite(legacyId)) {
    return String(legacyId);
  }

  return undefined;
};

const loadComposerWorksInternal = createAsyncThunk<
  LoadComposerWorksSuccess,
  LoadComposerWorksArgs,
  { state: RootState; rejectValue: { composerId: string; error: string } }
>(
  'composerWorks/load',
  async ({ composerId, spotifyArtistId, limit, offset }, { rejectWithValue, getState }) => {
    const LOG_PREFIX = '[ComposerWorksThunk]';
    const requestedLimit = sanitizeLimit(limit);
    const requestedOffset = sanitizeOffset(offset);

    try {
      const state = getState();
      const existingEntry = state.composerWorks?.byComposerId?.[composerId];
      const resolvedId = existingEntry?.resolvedComposerId ?? composerId;

      console.info(`${LOG_PREFIX} fetching /catalog/works`, {
        composerId: resolvedId,
        limit: requestedLimit,
        offset: requestedOffset
      });
      let response = await worksService.fetchComposerWorks({
        composerId: resolvedId,
        limit: requestedLimit,
        offset: requestedOffset,
      });
      console.info(`${LOG_PREFIX} received /catalog/works response`, {
        composerId: resolvedId,
        worksCount: response.works.length,
        pagination: response.pagination
      });
      let effectiveComposerId = resolvedId;
      let fullCollection: ComposerWork[] | undefined;

      const shouldAttemptFallback =
        response.works.length === 0 &&
        requestedOffset === 0 &&
        resolvedId === composerId &&
        spotifyArtistId;

      if (shouldAttemptFallback) {
        console.info(`${LOG_PREFIX} attempting spotify fallback`, {
          composerId,
          spotifyArtistId
        });
        const catalogComposer = await composerService.fetchBySpotifyId(spotifyArtistId);
        const catalogComposerId = resolveComposerId(catalogComposer);
        if (catalogComposerId && catalogComposerId !== composerId) {
          console.info(`${LOG_PREFIX} retrying /catalog/works with fallback composerId`, {
            originalComposerId: composerId,
            fallbackComposerId: catalogComposerId,
            limit: requestedLimit,
            offset: requestedOffset
          });
          response = await worksService.fetchComposerWorks({
            composerId: catalogComposerId,
            limit: requestedLimit,
            offset: requestedOffset,
          });
          effectiveComposerId = catalogComposerId;
        }
      }

      const effectiveLimit =
        requestedLimit ?? (response.pagination?.limit && response.pagination.limit > 0
          ? response.pagination.limit
          : DEFAULT_PAGE_SIZE);
      // TODO(COMPOSER_PAGINATION): remove client-side slicing once backend pagination is enforced.
      // Until backend pagination ships, the API may still return the full catalogue. When that happens
      // we locally slice the requested window, but keep the full list available so other experiences
      // (popular works, etc.) continue to work.
      const shouldSliceClientSide =
        effectiveLimit > 0 && response.works.length > effectiveLimit;

      const pageWorks = shouldSliceClientSide
        ? response.works.slice(requestedOffset, requestedOffset + effectiveLimit)
        : response.works;

      if (shouldSliceClientSide) {
        fullCollection = response.works;
      }

      return {
        composerId,
        works: pageWorks,
        pagination: response.pagination,
        resolvedComposerId: effectiveComposerId !== composerId ? effectiveComposerId : existingEntry?.resolvedComposerId,
        fullCollection,
      };
    } catch (error: any) {
      const message =
        error?.response?.data?.error ??
        error?.message ??
        'Failed to load works';
      return rejectWithValue({ composerId, error: String(message) });
    }
  },
  {
    condition: ({ composerId, force, offset, limit }, { getState }) => {
      if (!composerId) return false;
      if (force) return true;

      const state = getState();
      const entry = state.composerWorks?.byComposerId?.[composerId];

      if (!entry) return true;

      const requestedOffset = sanitizeOffset(offset);
      const requestedLimit = sanitizeLimit(limit);
      const limitForPage = requestedLimit ?? entry.pagination?.limit ?? DEFAULT_PAGE_SIZE;
      const targetPage = resolvePageNumber(requestedOffset, limitForPage);

      if (entry.status === 'loading' && entry.lastRequest) {
        const inFlightLimit = entry.lastRequest.limit ?? entry.pagination?.limit ?? DEFAULT_PAGE_SIZE;
        const inFlightPage = resolvePageNumber(entry.lastRequest.offset, inFlightLimit);
        if (inFlightPage === targetPage) {
          return false;
        }
      }

      if (entry.pages[targetPage]) {
        return false;
      }

      return true;
    },
  }
);

const initialState: ComposerWorksState = {
  byComposerId: {},
};

const composerWorksSlice = createSlice({
  name: 'composerWorks',
  initialState,
  reducers: {
    clearComposerWorks(state, action: PayloadAction<{ composerId: string }>) {
      delete state.byComposerId[action.payload.composerId];
    },
    resetComposerWorks(state) {
      state.byComposerId = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadComposerWorksInternal.pending, (state, action) => {
      const { composerId } = action.meta.arg;
      const entry = ensureEntry(state, composerId);
      entry.error = null;
      const requestedLimit = sanitizeLimit(action.meta.arg.limit);
      const requestedOffset = sanitizeOffset(action.meta.arg.offset);
      const limitForPage = requestedLimit ?? entry.pagination?.limit ?? DEFAULT_PAGE_SIZE;
      const targetPage = resolvePageNumber(requestedOffset, limitForPage);
      const isPageCached = Boolean(entry.pages[targetPage]);
      if (entry.status !== 'succeeded' || !isPageCached || action.meta.arg.force) {
        entry.status = 'loading';
      }
      entry.lastRequest = {
        offset: requestedOffset,
        limit: requestedLimit,
      };
    });

    builder.addCase(loadComposerWorksInternal.fulfilled, (state, action) => {
      const { composerId, works, pagination, resolvedComposerId, fullCollection } = action.payload;
      const entry = ensureEntry(state, composerId);
      const safeLimit = pagination?.limit && pagination.limit > 0 ? pagination.limit : DEFAULT_PAGE_SIZE;
      const safeOffset = pagination?.offset && pagination.offset >= 0 ? pagination.offset : 0;
      const page = resolvePageNumber(safeOffset, safeLimit);
      entry.pages[page] = works;
      const ensuredTotal = Math.max(
        pagination?.total ?? 0,
        safeOffset + works.length
      );
      entry.pagination = {
        total: ensuredTotal,
        limit: safeLimit,
        offset: safeOffset,
      };
      entry.items = aggregatePages(entry.pages);
      if (fullCollection && fullCollection.length) {
        // TODO(COMPOSER_PAGINATION): drop the fullCatalog shim when backend pagination is enforced.
        entry.fullCatalog = fullCollection.slice();
      }
      entry.status = 'succeeded';
      entry.error = null;
      entry.lastRequest = undefined;
      if (resolvedComposerId) {
        entry.resolvedComposerId = resolvedComposerId;
      }
    });

    builder.addCase(loadComposerWorksInternal.rejected, (state, action) => {
      if (action.meta.aborted) return;

      const { composerId } = action.meta.arg;
      const entry = ensureEntry(state, composerId);
      entry.status = 'failed';
      entry.lastRequest = undefined;

      if (action.payload?.error) {
        entry.error = action.payload.error;
      } else {
        entry.error = action.error.message ?? 'Failed to load works';
      }
    });
  },
});

const loadComposerWorksWithArgs = (
  composerId: string,
  options?: LoadComposerWorksOptions
) =>
  loadComposerWorksInternal({
    composerId,
    force: options?.force,
    spotifyArtistId: options?.spotifyArtistId,
    limit: options?.limit,
    offset: options?.offset,
  });

export const loadComposerWorks = Object.assign(loadComposerWorksWithArgs, {
  pending: loadComposerWorksInternal.pending,
  fulfilled: loadComposerWorksInternal.fulfilled,
  rejected: loadComposerWorksInternal.rejected,
  typePrefix: loadComposerWorksInternal.typePrefix,
});

export const selectComposerWorks =
  (composerId: string) =>
  (state: RootState): ComposerWork[] =>
    state.composerWorks.byComposerId[composerId]?.fullCatalog ??
    state.composerWorks.byComposerId[composerId]?.items ??
    [];

export const selectComposerWorksStatus =
  (composerId: string) =>
  (state: RootState): ComposerWorkStatus =>
    state.composerWorks.byComposerId[composerId]?.status ?? 'idle';

export const selectComposerWorksError =
  (composerId: string) =>
  (state: RootState): string | null =>
    state.composerWorks.byComposerId[composerId]?.error ?? null;

export const selectComposerWorksPagination =
  (composerId: string) =>
  (state: RootState): ComposerWorksPagination | null =>
    state.composerWorks.byComposerId[composerId]?.pagination ?? null;

export const selectComposerWorksPage =
  (composerId: string, page: number) =>
  (state: RootState): ComposerWork[] => {
    const entry = state.composerWorks.byComposerId[composerId];
    if (!entry) return [];
    const safePage = Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1;
    return entry.pages[safePage] ?? [];
  };

export const selectComposerWorksIsPageLoading =
  (composerId: string, page: number) =>
  (state: RootState): boolean => {
    const entry = state.composerWorks.byComposerId[composerId];
    if (!entry || entry.status !== 'loading' || !entry.lastRequest) {
      return false;
    }

    const limit = entry.lastRequest.limit ?? entry.pagination?.limit ?? DEFAULT_PAGE_SIZE;
    const inFlightPage = resolvePageNumber(entry.lastRequest.offset, limit);
    const safePage = Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1;
    return inFlightPage === safePage;
  };

export const composerWorksActions = {
  ...composerWorksSlice.actions,
  loadComposerWorks,
};

export default composerWorksSlice.reducer;
